import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, KeyRound, Lock, Mail, ShieldCheck, X } from 'lucide-react';
import { passwordProblems, passwordScore, PASSWORD_MIN } from '@tradeoutlet/shared';
import { ApiError } from '@tradeoutlet/shared';
import { BrandMark } from '@/components/brand/BrandMark';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { supabase } from '@/lib/supabase';
import { Button, Card, ErrorBox, Field, Input, Modal, Spinner, ask } from '@/components/ui';
import { APP_SHORT_NAME } from '@tradeoutlet/shared';
import { useBrand } from '@/lib/branding';
import { emailLinkToken } from '@/lib/linkTokens';
import { MemberMfaRow, TwoFactorSection } from './mfa';
import { EditableHint } from '@/components/ui/EditableHint';

export const securityKey = ['me', 'security'] as const;
export function useSecurityStatus(enabled = true) {
  return useQuery({ queryKey: securityKey, queryFn: () => api.securityStatus(), enabled, staleTime: 60_000, retry: false });
}

const SCORE = [
  { label: 'Too weak', bar: 'bg-rose-500' }, { label: 'Weak', bar: 'bg-rose-500' }, { label: 'Fair', bar: 'bg-amber-500' },
  { label: 'Good', bar: 'bg-emerald-500' }, { label: 'Strong', bar: 'bg-emerald-600' },
];

function PasswordInput({ value, onChange, autoComplete, label, testId, hint }: { value: string; onChange: (v: string) => void; autoComplete: string; label: string; testId: string; hint?: string }) {
  const [show, setShow] = useState(false);
  return (
    <Field label={label} hint={hint}>
      <div className="relative">
        <Input type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} aria-label={label} className="pr-10" />
        <button type="button" onClick={() => setShow(s => !s)} className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-500 hover:text-slate-600" aria-label={show ? 'Hide password' : 'Show password'}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </Field>
  );
}

/** New + confirm fields with the live strength meter and rule checklist. `valid` is true when both are acceptable. */
export function NewPasswordFields({ value, confirm, onChange, onConfirm, ctx }: {
  value: string; confirm: string; onChange: (v: string) => void; onConfirm: (v: string) => void; ctx: { email?: string | null; name?: string | null; current?: string | null };
}) {
  const problems = value ? passwordProblems(value, ctx) : [];
  const score = passwordScore(value);
  const mismatch = confirm.length > 0 && confirm !== value;
  return (
    <div className="space-y-3">
      <PasswordInput label="New password" value={value} onChange={onChange} autoComplete="new-password" testId="new-password" hint={`At least ${PASSWORD_MIN} characters`} />
      {value && (
        <div data-testid="strength">
          <div className="flex gap-1">{[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i < score ? SCORE[score].bar : 'bg-slate-200 dark:bg-slate-700'}`} />)}</div>
          <div className="mt-1 text-xs text-slate-500">Strength: <span className="font-medium">{SCORE[score].label}</span></div>
          {problems.length > 0 && <ul className="mt-1.5 space-y-0.5 text-xs text-rose-700 dark:text-rose-300">{problems.map(p => <li key={p} className="flex items-start gap-1"><X className="mt-0.5 h-3 w-3 shrink-0" />{p}</li>)}</ul>}
        </div>
      )}
      <PasswordInput label="Confirm new password" value={confirm} onChange={onConfirm} autoComplete="new-password" testId="confirm-password" />
      {mismatch && <p className="text-xs text-rose-700 dark:text-rose-300">The passwords don't match.</p>}
      {!mismatch && confirm && confirm === value && problems.length === 0 && <p className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" />Passwords match</p>}
    </div>
  );
}
export const newPasswordOk = (pw: string, confirm: string, ctx: Parameters<typeof passwordProblems>[1]) => Boolean(pw) && pw === confirm && passwordProblems(pw, ctx).length === 0;

/** Settings > Security. */
export function SecurityCard() {
  const { mode } = useAuth();
  const me = useMe();
  const toast = useUi(s => s.toast);
  const status = useSecurityStatus();
  const available = mode === 'supabase' || Boolean(status.data?.password_auth);
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const ctx = { email: me.data?.profile?.email, name: me.data?.profile?.display_name, current };
  const ok = Boolean(current) && newPasswordOk(pw, confirm, ctx);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      const r = await api.changePassword({ current_password: current, new_password: pw, sign_out_others: true });
      setCurrent(''); setPw(''); setConfirm('');
      void status.refetch();
      toast(r.signed_out_others ? 'Password changed. Your other devices have been signed out.' : 'Password changed', 'success');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  // t363u: ends every other session now; their open tabs and apps go to sign in straight away.
  const signOutOthers = async () => {
    if (!await ask('Sign out of the Hub on all your other devices and browsers? You stay signed in here.')) return;
    setSigningOut(true);
    try {
      await api.signOutOtherDevices();
      toast('Signed out of your other devices', 'success');
    } catch (err) { toast(errorMessage(err), 'error'); } finally { setSigningOut(false); }
  };

  return (
    <Card title={<span id="security" className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-slate-500" />Security</span>}>
      <h3 className="mb-1 text-sm font-semibold">Change password</h3>
      {!available ? (
        <p className="text-sm text-slate-500">You're using the local development login, which has no password. Password changes work when the app runs with Supabase Auth.</p>
      ) : (
        <form onSubmit={e => void submit(e)} className="max-w-md space-y-3" data-testid="change-password">
          {status.data?.password_changed_at && (
            <p className="text-xs text-slate-500">Last changed {new Date(status.data.password_changed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' })}.</p>
          )}
          <PasswordInput label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" testId="current-password" />
          <NewPasswordFields value={pw} confirm={confirm} onChange={setPw} onConfirm={setConfirm} ctx={ctx} />
          <p className="text-xs text-slate-600 dark:text-slate-300">You stay signed in here; your other devices and browsers are signed out.</p>
          <ErrorBox error={error} />
          <Button type="submit" loading={busy} disabled={!ok}><KeyRound className="h-4 w-4" />Change password</Button>
        </form>
      )}
      {available && (
        <div className="mt-6 max-w-md space-y-2" data-testid="sign-out-others">
          <h3 className="text-sm font-semibold">Other devices</h3>
          <EditableHint as="p" id="account.security.sign-out-everywhere" className="text-xs text-slate-500" default="Lost a phone or signed in on a shared computer? This signs you out everywhere except here, straight away." />
          <Button variant="secondary" loading={signingOut} onClick={() => void signOutOthers()}><Lock className="h-4 w-4" />Sign out other devices</Button>
        </div>
      )}
      {mode === 'supabase' && <TwoFactorSection />}
    </Card>
  );
}

export function AuthFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="on-dark relative flex min-h-full items-center justify-center overflow-hidden bg-ink p-6">
      <div className="absolute inset-x-0 top-0 h-2 bg-brand-yellow" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="mb-7 flex justify-center"><BrandMark height={44} /></div>
        <div className="on-page rounded-2xl bg-white p-7 shadow-2xl ring-1 ring-white/10 dark:bg-slate-900">
          <h1 className="mb-4 text-lg font-bold">{title}<span className="mt-1.5 block h-1 w-10 rounded-full bg-accent-line" aria-hidden /></h1>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Shown instead of the app while an admin-set temporary password is still in use. */
export function ForcedPasswordChange() {
  const me = useMe();
  const { signOut } = useAuth();
  const toast = useUi(s => s.toast);
  const status = useSecurityStatus();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctx = { email: me.data?.profile?.email, name: me.data?.profile?.display_name };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.forcedPasswordChange({ new_password: pw, sign_out_others: true });
      toast('Password changed. Welcome back!', 'success');
      await status.refetch();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <AuthFrame title="Choose a new password">
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">An admin set a temporary password for your account. Choose your own password to continue.</p>
      <form onSubmit={e => void submit(e)} className="space-y-4" data-testid="forced-change">
        <NewPasswordFields value={pw} confirm={confirm} onChange={setPw} onConfirm={setConfirm} ctx={ctx} />
        <ErrorBox error={error} />
        <Button type="submit" className="w-full" loading={busy} disabled={!newPasswordOk(pw, confirm, ctx)}>Save and continue</Button>
        <button type="button" className="w-full text-center text-sm text-slate-500 hover:underline" onClick={() => void signOut()}>Sign out</button>
      </form>
    </AuthFrame>
  );
}

/** "Forgot password?" panel on the sign-in page. */
export function ForgotPassword({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const auth = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy'); setError(null);
    try { await auth.requestPasswordReset(email); setState('sent'); } catch (err) {
      const m = errorMessage(err);
      if (/rate|seconds|too many/i.test(m)) { setError('A reset email was sent recently. Wait a minute and try again.'); setState('idle'); }
      else if (/offline|network|failed to fetch/i.test(m)) { setError(m); setState('idle'); }
      // t402u: anything else looks exactly like success, so the form can't tell who has an account.
      else setState('sent');
    }
  };
  return (
    <div data-testid="forgot-password">
      <h1 className="mb-2 text-lg font-bold">Reset your password<span className="mt-1.5 block h-1 w-10 rounded-full bg-accent-line" aria-hidden /></h1>
      {state === 'sent' ? (
        <div className="space-y-4">
          <div className="flex gap-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:ring-emerald-900">
            <Mail className="mt-0.5 h-4 w-4 shrink-0" />
            <span data-testid="reset-sent">If an account exists, we've sent a link to reset the password to <strong>{email}</strong>. It works once and expires in an hour. Check spam if it doesn't arrive.</span>
          </div>
          <Button variant="secondary" className="w-full" onClick={onBack}>Back to sign in</Button>
        </div>
      ) : (
        <form onSubmit={e => void submit(e)} className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">Enter your email address and we'll send you a link to choose a new password.</p>
          <Field label="Email address"><Input type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="reset-email" /></Field>
          <ErrorBox error={error} />
          <Button type="submit" className="w-full" loading={state === 'busy'}>Send reset link</Button>
          <button type="button" className="w-full text-center text-sm text-slate-500 hover:underline" onClick={onBack}>Back to sign in</button>
        </form>
      )}
    </div>
  );
}

/**
 * Link-token params of an emailed or shared link: #token_hash=...&type=recovery|invite (t172/t173: in the fragment, so it never reaches
 * a server log) or ?token_hash=... (links sent before then, until they expire).
 */
function linkToken(expected: 'recovery' | 'invite') {
  return emailLinkToken(window.location.hash, window.location.search, expected);
}

/**
 * Turns the emailed token into a session only when the person submits, so mail scanners that open links can't use
 * them up. Throws a readable error when the link is invalid, expired or already used.
 */
async function verifyLink(token: string, type: 'recovery' | 'invite') {
  if (!supabase) throw new Error('This server has no Supabase Auth, so emailed links can’t be used here.');
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: token, type });
  if (error || !data.session) throw new Error(type === 'invite' ? 'This invite link has expired or was already used. Ask the person who invited you to send a new one.' : 'This reset link has expired or was already used. Request a new one from the sign-in page.');
  window.history.replaceState(null, '', window.location.pathname);
  return data.session;
}

/**
 * /reset-password: the landing page of the emailed link. Hub emails carry #token_hash= (t173; ?token_hash= before), verified on submit; links sent
 * by Supabase Auth itself use PKCE (?code=, handled by supabase-js) or tokens in the hash, which we turn into a session.
 */
export function ResetPasswordPage() {
  const nav = useNavigate();
  const toast = useUi(s => s.toast);
  const [link] = useState(() => linkToken('recovery'));
  const [phase, setPhase] = useState<'checking' | 'ready' | 'invalid'>(link ? 'ready' : 'checking');
  const [email, setEmail] = useState<string | null>(null);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (link) return;
    if (!supabase) { setPhase('invalid'); return; }
    let cancelled = false;
    void (async () => {
      const hash = new URLSearchParams(window.location.hash.slice(1));
      const qs = new URLSearchParams(window.location.search);
      if (hash.get('error_description') || qs.get('error_description')) { setPhase('invalid'); setError(hash.get('error_description') ?? qs.get('error_description')); return; }
      const at = hash.get('access_token'), rt = hash.get('refresh_token');
      if (at && rt) {
        await supabase!.auth.setSession({ access_token: at, refresh_token: rt });
        window.history.replaceState(null, '', '/reset-password');
      }
      // PKCE links are exchanged by supabase-js during start-up; wait briefly for it.
      for (let i = 0; i < 20 && !cancelled; i++) {
        const { data } = await supabase!.auth.getSession();
        if (data.session) { setEmail(data.session.user.email ?? null); setPhase('ready'); return; }
        await new Promise(r => setTimeout(r, 150));
      }
      if (!cancelled) setPhase('invalid');
    })();
    return () => { cancelled = true; };
  }, [link]);

  const ctx = { email };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (link) await verifyLink(link.token, 'recovery');
      await api.completePasswordRecovery(pw);
      toast('Password changed. You are signed in.', 'success');
      nav('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'This reset link has expired. Request a new one from the sign-in page.' : errorMessage(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthFrame title="Choose a new password">
      {phase === 'checking' && <div className="flex justify-center py-6"><Spinner /></div>}
      {phase === 'invalid' && (
        <div className="space-y-4" data-testid="reset-invalid">
          <p className="text-sm text-slate-600 dark:text-slate-300">{error ?? 'This reset link is invalid or has expired.'} Request a new link from the sign-in page.</p>
          <Button className="w-full" onClick={() => nav('/signin?forgot=1', { replace: true })}>Back to sign in</Button>
        </div>
      )}
      {phase === 'ready' && (
        <form onSubmit={e => void submit(e)} className="space-y-4" data-testid="reset-form">
          {email && <p className="text-sm text-slate-600 dark:text-slate-300">Resetting the password for <strong>{email}</strong>.</p>}
          <NewPasswordFields value={pw} confirm={confirm} onChange={setPw} onConfirm={setConfirm} ctx={ctx} />
          <ErrorBox error={error} />
          <Button type="submit" className="w-full" loading={busy} disabled={!newPasswordOk(pw, confirm, ctx)}><ShieldCheck className="h-4 w-4" />Set password and sign in</Button>
        </form>
      )}
    </AuthFrame>
  );
}

/**
 * /accept-invite: the landing page of an invite email. Choosing a password verifies the link (creating the session),
 * sets the password, and the API applies the invitation (role, buildings, positions, job title) on that first request.
 */
export function AcceptInvitePage() {
  const nav = useNavigate();
  const toast = useUi(s => s.toast);
  const [link] = useState(() => linkToken('invite'));
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [used, setUsed] = useState(false);
  const ctx = { email: link?.email ?? null };
  const brand = useBrand();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!link) return;
    setBusy(true); setError(null);
    try {
      await verifyLink(link.token, 'invite');
      await api.completePasswordRecovery(pw);
      toast(`Welcome to ${brand.title}. Your password is set.`, 'success');
      window.location.replace('/');
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 403 ? 'This invitation is no longer valid (it may have been revoked or expired). Ask the person who invited you to send a new one.' : errorMessage(err);
      setError(msg); setUsed(/already used|no longer valid|expired/.test(msg));
    } finally { setBusy(false); }
  };
  return (
    <AuthFrame title={`Join ${brand.title}`}>
      {!link ? (
        <div className="space-y-4" data-testid="invite-invalid">
          <p className="text-sm text-slate-600 dark:text-slate-300">This invite link is incomplete. Open it again from the email, or ask the person who invited you to resend it.</p>
          <Button className="w-full" onClick={() => nav('/signin', { replace: true })}>Go to sign in</Button>
        </div>
      ) : (
        <form onSubmit={e => void submit(e)} className="space-y-4" data-testid="accept-invite-form">
          <p className="text-sm text-slate-600 dark:text-slate-300">Choose a password{link.email ? <> for <strong>{link.email}</strong></> : ''}. You’ll go straight into {APP_SHORT_NAME} afterwards, set up with the role and buildings you were invited with.</p>
          <NewPasswordFields value={pw} confirm={confirm} onChange={setPw} onConfirm={setConfirm} ctx={ctx} />
          <ErrorBox error={error} />
          <Button type="submit" className="w-full" loading={busy} disabled={!newPasswordOk(pw, confirm, ctx)} data-testid="accept-invite-submit"><ShieldCheck className="h-4 w-4" />Set password and open the Hub</Button>
          {used && <button type="button" className="link w-full text-center text-sm" onClick={() => nav('/signin', { replace: true })}>Already set a password? Sign in</button>}
        </form>
      )}
    </AuthFrame>
  );
}

/** Admin > Members > Password: send a reset email or set a temporary password (needs admin.users.password). */
export function MemberPasswordModal({ ws, member, onClose }: { ws: string; member: { user_id: string; display_name: string; email: string } | null; onClose: () => void }) {
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState<'reset' | 'temp' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [temp, setTemp] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const sec = useQuery({ queryKey: ['member-security', ws, member?.user_id], queryFn: () => api.memberSecurity(ws, member!.user_id), enabled: Boolean(member) });
  useEffect(() => { setTemp(null); setError(null); setCopied(false); }, [member?.user_id]);
  if (!member) return null;
  const sendReset = async () => {
    setBusy('reset'); setError(null);
    try { const r = await api.sendMemberPasswordReset(ws, member.user_id); toast(`Reset link sent to ${r.email}`, 'success'); onClose(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(null); void sec.refetch(); }
  };
  const setTemporary = async () => {
    if (!await ask(`Set a temporary password for ${member.display_name}? Their current password stops working and they're signed out of other devices.`)) return;
    setBusy('temp'); setError(null);
    try { const r = await api.setTemporaryPassword(ws, member.user_id); setTemp(r.temporary_password); toast('Temporary password set', 'success'); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(null); void sec.refetch(); }
  };
  const fmt = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null);
  return (
    <Modal open onClose={onClose} title={`Password · ${member.display_name}`}>
      <div className="space-y-4" data-testid="member-password">
        <div className="text-xs text-slate-500">
          {member.email}
          {sec.data?.must_change_password && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">Must change password at next sign-in</span>}
          {fmt(sec.data?.password_changed_at) && <div>Password last changed {fmt(sec.data?.password_changed_at)}</div>}
          {fmt(sec.data?.reset_requested_at) && <div>Reset email last sent {fmt(sec.data?.reset_requested_at)}</div>}
        </div>
        {temp ? (
          <div className="space-y-2 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700" data-testid="temp-password">
            <div className="text-sm font-medium">Temporary password</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-base tracking-wide ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">{temp}</code>
              <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(temp).then(() => setCopied(true))}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <p className="text-xs text-slate-500">Shown once - give it to {member.display_name} in person or by phone. They'll be asked to choose their own password when they sign in.</p>
          </div>
        ) : (
          <>
            <div className="rounded-lg p-3 ring-1 ring-slate-200 dark:ring-slate-700">
              <div className="text-sm font-medium">Send a reset link</div>
              <p className="mb-2 text-xs text-slate-500">Emails {member.email} a one-time link to choose a new password. Needs email sending (SMTP) on the server.</p>
              <Button size="sm" variant="secondary" loading={busy === 'reset'} onClick={() => void sendReset()}><Mail className="h-4 w-4" />Send reset email</Button>
            </div>
            {sec.data && <div className="rounded-lg p-3 ring-1 ring-slate-200 dark:ring-slate-700"><MemberMfaRow ws={ws} userId={member.user_id} factors={sec.data.mfa_factors ?? 0} onChanged={() => void sec.refetch()} /></div>}
            <div className="rounded-lg p-3 ring-1 ring-slate-200 dark:ring-slate-700">
              <div className="text-sm font-medium">Set a temporary password</div>
              <p className="mb-2 text-xs text-slate-500">Generates a password you pass on yourself. It must be changed at next sign-in, and other sessions are signed out.</p>
              <Button size="sm" loading={busy === 'temp'} onClick={() => void setTemporary()}><KeyRound className="h-4 w-4" />Set temporary password</Button>
            </div>
          </>
        )}
        <ErrorBox error={error} />
        <p className="text-xs text-slate-500">These actions are recorded in the audit log.</p>
      </div>
    </Modal>
  );
}
